import React, {useContext, useEffect, useState} from "react";
import {SettingsContext} from "../../../context/SettingsProvider";
import FileDetailsModal from "../../../components/modals/FileDetailsModal";
import Draggable from "react-draggable";
import DeleteModal from "../../../components/modals/DeleteModal";
import SuccessNotification from "../../../components/toast/SuccessNotification";
import ErrorNotification from "../../../components/toast/ErrorNotification";
// import { FaEdit } from "react-icons/fa";

const FileDetails = () => {
  const { getFileTypes, fileTypes, setFileTypes, postFileTypes, updateFileType, deleteFileType } = useContext(SettingsContext);
  const [isOpen, setIsOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [fileToEdit, setFileToEdit] = useState({
    id: null,
    type: "",
    description: "",
    required: false,
    scope: "false",
    expires: "",
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileTypeToDelete, setFileTypeToDelete] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [text, setText] = useState("");
  const [newFile, setNewFile] = useState({
    type: "",
    description: "",
    expires: "",
    required: false,
    scope: "false"
  });

  const closeNotification = () => {
    setShowSuccess(false);
    setShowError(false);
  };

  const openModal = () => {
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  const handleNewFileChange = (e) => {
    setNewFile({...newFile, [e.target.name]: e.target.value});
  };

  const handleCheckboxChange = (e) => {
    const name = e.target.name;
    const checked = e.target.checked;
    setNewFile({...newFile, [name]: name === 'required' ? checked : String(checked)});
  };

  const addNewFile = async () => {
    if (
      !newFile.type ||
      !newFile.description ||
      !newFile.expires
    ) {
      setShowError(true);
      setText('Please fill in all fields.');
      return;
    }
    const expiresAsInt = parseInt(newFile.expires, 10);
    const payload = {
      ...newFile,
      expires: expiresAsInt,
      visible: true,
      signatures: []
    };
    try {
      await postFileTypes(payload);
      setShowSuccess(true);
      setText('Successfully added a new file type.');
      closeModal();
      setNewFile({
        type: "",
        description: "",
        required: false,
        scope: "false",
        expires: "",
        visible: true,
        signatures: []
      });
    } catch (error) {
      setShowError(true);
      setText('Failed to add new file type.');
      console.error("An error occurred while posting data:", error);
    }
  };

  const updateType = async (index, type, checked) => {
    const newFileTypes = [...fileTypes];
    newFileTypes[index] = {...fileTypes[index], visible: checked};
    setFileTypes(newFileTypes);
    await updateFileType(newFileTypes);
  };

  const handleDeleteComment = (id) => {
    setFileTypeToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirmation = async () => {
    if (fileTypeToDelete === null) {
      setShowError(true);
      setText('No file detail selected for deletion.');
      console.error("No file detail to delete");
      return;
    }
    try {
      await deleteFileType(fileTypeToDelete);
      const updatedFileTypes = fileTypes.filter((ft) => ft.id !== fileTypeToDelete);
      setFileTypes(updatedFileTypes);
      setShowDeleteModal(false);
      setFileTypeToDelete(null);
      setShowSuccess(true);
      setText('Successfully deleted the file detail.');
    } catch (error) {
      setShowError(true);
      setText('Failed to delete file detail.');
      console.error("An error occurred while deleting:", error);
    }
  };

  // const openEditModal = (fileType) => {
  //   setFileToEdit(fileType);
  //   setIsEditModalOpen(true);
  // };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setFileToEdit({
      id: null,
      type: "",
      description: "",
      required: false,
      scope: "false",
      expires: "",
    });
  };

  const handleEditFileChange = (e) => {
    setFileToEdit({...fileToEdit, [e.target.name]: e.target.value});
  };

  const handleEditCheckboxChange = (e) => {
    const name = e.target.name;
    const checked = e.target.checked;
    setFileToEdit({...fileToEdit, [name]: name === 'required' ? checked : String(checked)});
  };

  const saveEditFile = async () => {
    if (!fileToEdit.type || !fileToEdit.description || !fileToEdit.expires) {
      setShowError(true);
      setText('Please fill in all fields.');
      return;
    }
    const expiresAsInt = parseInt(fileToEdit.expires, 10);
    const updatedPayload = {
      ...fileToEdit,
      expires: expiresAsInt,
    };
    try {
      const updatedFileTypes = fileTypes.map((f) => f.id === fileToEdit.id ? updatedPayload : f);
      setFileTypes(updatedFileTypes);
      await updateFileType(updatedFileTypes);
      setShowSuccess(true);
      setText('Successfully updated the file type.');
      closeEditModal();
    } catch (error) {
      setShowError(true);
      setText('Failed to update file type.');
      console.error("An error occurred while updating file type:", error);
    }
  };

  useEffect(() => {
    getFileTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div id="filedetails" className="mt-3 mx-10 mb-3 w-3/4 max-w-4xl shadow overflow-hidden sm:rounded-lg">
      <div className="p-2 flex justify-between bg-gray-400 dark:bg-stone-700">
        <button
          onClick={openModal}
          className="bg-blue-600 hover:bg-blue-500 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white font-semibold px-2 py-1 rounded"
        >
          Add File Types
        </button>
      </div>
      <div className="max-h-[90vh] overflow-y-scroll">
        <table className="min-w-full">
          <thead
            className="bg-gray-500 dark:bg-stone-500 sticky top-0 z-50">
          <tr>
            <th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider select-none">
              File Type
            </th>
            <th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider select-none">
              Description
            </th>
            <th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider select-none">
            </th>
            <th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider select-none">
              Expiration
            </th>
            <th
              className="px-2 py-3 w-10 text-left text-xs font-medium text-white uppercase tracking-wider select-none">
            </th>
            <th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider select-none">
            </th>
          </tr>
          </thead>
          <tbody
            className="bg-white divide-y divide-gray-200 dark:bg-stone-900 dark:divide-gray-600 dark:text-gray-300">
          {fileTypes.map((fileType, index) => (
            <tr key={fileType.id}>
              <td className="px-2 py-4 whitespace-nowrap">
                <div className="text-sm font-medium select-none">
                  {fileType.type}
                </div>
              </td>
              <td className="px-2 py-4">
                <div className="text-sm select-none">
                  {fileType.description}
                </div>
              </td>
              <td className="px-2 py-4">
                <div className="text-sm select-none">
                  {fileType.required}
                </div>
              </td>
              <td className="px-2 py-4">
                <div className="text-sm select-none">
                  {fileType.expires ? `${fileType.expires} days` : "None"}
                </div>
              </td>
              <td className="px-2 py-2 whitespace-nowrap select-none z-10">
                <label
                  className={`switch inline-flex items-center w-20 px-1 rounded-md cursor-pointer select-none ${
                    fileType.visible ? "bg-blue-400 dark:bg-indigo-500" : "bg-gray-200 dark:bg-gray-500"
                  }`}
                >
                  <input
                    className="hidden"
                    type="checkbox"
                    checked={fileType.visible}
                    onChange={() =>
                      updateType(index, fileType.name, !fileType.visible)
                    }
                  />
                  <span
                    className={`block w-4 h-4 bg-white rounded-full shadow-inner transition-transform duration-200 ease-in-out select-none ${
                      fileType.visible ? "transform translate-x-14" : ""
                    }`}
                  />
                  <span
                    className={`text-xs select-none ${
                      fileType.visible ? "text-gray-100" : "text-gray-400"
                    }`}
                  >
                    {fileType.visible ? "Visible" : "Hidden"}
                  </span>
                </label>
              </td>
              <td className="px-2 py-4 flex items-center gap-2">
                {/*<button*/}
                {/*  onClick={() => openEditModal(fileType)}*/}
                {/*  className="text-blue-400 hover:text-blue-500 dark:text-indigo-400 dark:hover:text-indigo-500 font-semibold px-2 py-1 rounded"*/}
                {/*>*/}
                {/*  <FaEdit className="w-5 h-5" />*/}
                {/*</button>*/}
                <button
                  onClick={() => handleDeleteComment(fileType.id)}
                  className="text-red-400 hover:text-red-500 font-semibold px-2 py-1 rounded"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                    />
                  </svg>
                </button>
              </td>
            </tr>
          ))}
          <tr></tr>
          </tbody>
          {isOpen && (
            <Draggable>
              <FileDetailsModal isOpen={isOpen} onClose={closeModal} onSave={addNewFile} title={"Add File Details"}>
                <div className="mb-10">
                  <input
                    name="type"
                    value={newFile.type}
                    maxLength="32"
                    onChange={handleNewFileChange}
                    className="w-full my-2 px-2 py-1 border bg-gray-100 border-gray-300 text-gray-700 dark:bg-stone-700 dark:border-stone-700 rounded dark:text-gray-300"
                    placeholder={"Name"}
                  />
                  <textarea
                    name="description"
                    value={newFile.description}
                    maxLength="1024"
                    onChange={handleNewFileChange}
                    className="w-full my-2 px-2 py-1 border bg-gray-100 border-gray-300 text-gray-700 dark:bg-stone-700 dark:border-stone-700 rounded dark:text-gray-300"
                    placeholder="Description"
                    rows={3}
                    style={{resize: 'none'}}
                  />
                  <div className="checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        name="scope"
                        checked={newFile.scope === "true"}
                        onChange={handleCheckboxChange}
                        className="mx-2 rounded text-blue-600 focus:ring-blue-200"
                      />
                      Website
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        name="required"
                        checked={newFile.required}
                        onChange={handleCheckboxChange}
                        className="mx-2 rounded text-blue-600 focus:ring-blue-200"
                      />
                      Vendor
                    </label>
                  </div>
                  <select
                    name="expires"
                    value={newFile.expires}
                    onChange={handleNewFileChange}
                    className="w-full my-2 px-2 py-1.5 border bg-gray-100 border-gray-300 text-gray-700 dark:bg-stone-700 dark:border-stone-700 dark:text-gray-300 rounded"
                  >
                    <option value={0}>None</option>
                    <option value={30}>30 days</option>
                    <option value={365}>365 days</option>
                  </select>
                </div>
              </FileDetailsModal>
            </Draggable>
          )}
          {isEditModalOpen && (
            <Draggable>
              <FileDetailsModal
                isOpen={isEditModalOpen}
                onClose={closeEditModal}
                onSave={saveEditFile}
                title={"Edit File Details"}
              >
                <div className="mb-10">
                  <input
                    name="type"
                    value={fileToEdit.type}
                    maxLength="32"
                    onChange={handleEditFileChange}
                    className="w-full my-2 px-2 py-1 border bg-gray-100 border-gray-300 text-gray-700 dark:bg-stone-700 dark:border-stone-700 rounded dark:text-gray-300"
                    placeholder={"Name"}
                  />
                  <textarea
                    name="description"
                    value={fileToEdit.description}
                    maxLength="1024"
                    onChange={handleEditFileChange}
                    className="w-full my-2 px-2 py-1 border bg-gray-100 border-gray-300 text-gray-700 dark:bg-stone-700 dark:border-stone-700 rounded dark:text-gray-300"
                    placeholder="Description"
                    rows={3}
                    style={{resize: 'none'}}
                  />
                  <div className="checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        name="scope"
                        checked={fileToEdit.scope === "true"}
                        onChange={handleEditCheckboxChange}
                        className="mx-2 rounded text-blue-600 focus:ring-blue-200"
                      />
                      Website
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        name="required"
                        checked={fileToEdit.required}
                        onChange={handleEditCheckboxChange}
                        className="mx-2 rounded text-blue-600 focus:ring-blue-200"
                      />
                      Vendor
                    </label>
                  </div>
                  <select
                    name="expires"
                    value={fileToEdit.expires}
                    onChange={handleEditFileChange}
                    className="w-full my-2 px-2 py-1.5 border bg-gray-100 border-gray-300 text-gray-700 dark:bg-stone-700 dark:border-stone-700 dark:text-gray-300 rounded"
                  >
                    <option value={0}>None</option>
                    <option value={30}>30 days</option>
                    <option value={365}>365 days</option>
                  </select>
                </div>
              </FileDetailsModal>
            </Draggable>
          )}
        </table>
      </div>
      {showDeleteModal && (
        <DeleteModal
          title="Are you sure?"
          onCancel={() => {
            setShowDeleteModal(false);
            setFileTypeToDelete(null);
          }}
          onDelete={handleDeleteConfirmation}
        />
      )}
      {showSuccess && (
        <SuccessNotification show={showSuccess} onClose={closeNotification} text={text}/>
      )}
      {showError && (
        <ErrorNotification show={showError} onClose={closeNotification} text={text}/>
      )}
    </div>
  );
};

export default FileDetails;
